/**
 * Regole pure su campi e stati: visibilità, obbligatorietà, valore vuoto.
 *
 * Niente store, niente i18n, nessun import: sono funzioni pure, riusabili e verificabili in
 * isolamento. Lo strato con stato (cache, etichette tradotte, accesso alle configurazioni) sta in
 * composables/useFieldRequirements.js.
 *
 * ATTENZIONE — queste regole esistono anche dentro ProjectForm.vue, dove operano su `formData`
 * (una bozza in corso di modifica) invece che su una riga salvata. I due usi non sono unificabili
 * senza far entrare semantica da form in un'API di sola lettura, quindi la duplicazione è voluta.
 * Se si cambia una regola, aggiornare anche l'originale (e viceversa):
 *
 *   isStatusVisibleFor  <- ProjectForm.vue  isStatusVisible  (~:1083)
 *   isFieldVisibleFor   <- ProjectForm.vue  visibleFields    (~:1469, blocco conditionalOn)
 *   isFieldRequiredAt   <- ProjectForm.vue  getFieldRules    (~:1861, blocco required)
 *                          e Backend ProjectService.RequiredWhenLeaving
 *   isFieldEmpty        <- ProjectForm.vue  getFieldRules    (~:1892, regole di obbligatorietà)
 */

// Tipi il cui valore è una lista: sono "vuoti" quando l'array non ha elementi
export const ARRAY_FIELD_TYPES = new Set(['multiselect', 'notelist', 'servicequantity'])

export const toInt = (value) => {
  const n = parseInt(value)
  return Number.isNaN(n) ? null : n
}

// Visibilità di uno stato. Nota: niente ramo array-`includes`, la grammatica conditionalOn degli
// stati è più stretta di quella dei campi. Non unificarle.
export const isStatusVisibleFor = (project, status) => {
  if (!status || !status.conditionalOn) return true

  const { field, value, values, notValue } = status.conditionalOn
  const fieldValue = project?.[field]

  if (notValue !== undefined && fieldValue === notValue) return false
  if (values && Array.isArray(values)) return values.includes(fieldValue)
  if (value !== undefined) return fieldValue === value

  return true
}

// Visibilità di un campo per una riga salvata: tipo di processo + conditionalOn. Manca di
// proposito il gate "nuovo processo senza tipo scelto" del form: qui il tipo c'è sempre.
export const isFieldVisibleFor = (project, field) => {
  if (!field?.name) return false
  if (field.name === 'activeStatus' || field.name === 'status') return false
  if (field.name === 'projectType') return true

  const projectTypeId = toInt(project?.projectType)
  if (Array.isArray(field.visibleInProjectTypes) && projectTypeId !== null &&
      !field.visibleInProjectTypes.includes(projectTypeId)) {
    return false
  }

  const condition = field.conditionalOn
  if (!condition) return true

  const currentValue = project?.[condition.field]

  if (condition.notValue !== undefined && currentValue === condition.notValue) return false
  if (Array.isArray(condition.values)) return condition.values.includes(currentValue)
  if (condition.value !== undefined) {
    // Su un campo multiselect la condizione è soddisfatta se il valore è fra quelli scelti
    return Array.isArray(currentValue)
      ? currentValue.includes(condition.value)
      : currentValue === condition.value
  }

  return true
}

// Obbligatorietà CUMULATIVA: un campo obbligatorio allo stato N resta obbligatorio da N in poi.
// '*' vale sempre, ma è consultato solo come ripiego quando manca la chiave esatta.
export const isFieldRequiredAt = (field, statusId) => {
  const rules = field?.validationByStatus
  if (!rules) return false

  if ((rules[String(statusId)] || rules['*'])?.required) return true

  const current = parseInt(statusId)
  if (Number.isNaN(current)) return false

  for (const key in rules) {
    if (key === '*') continue
    const keyNum = parseInt(key)
    if (!Number.isNaN(keyNum) && keyNum <= current && rules[key]?.required) return true
  }

  return false
}

// Un checkbox deve essere letteralmente true, una lista deve avere almeno un elemento.
export const isFieldEmpty = (project, field) => {
  const value = project?.[field.name]
  return ARRAY_FIELD_TYPES.has(field.type)
    ? !(Array.isArray(value) && value.length > 0)
    : !value
}

// ---------------------------------------------------------------------------------------------
// Condizioni di attivazione delle AZIONI IA (cfg.json -> aiActions[].conditionalOn e
// statusList[].verifications[].conditionalOn). Grammatica a LISTA in AND, più larga di quella di
// campi e stati qui sopra: ogni clausola è { field, value | notValue | values | isSet }.
//
// Perché non riusare isFieldVisibleFor: lì la condizione è una sola, il confronto è stretto (`===`)
// e l'editor dei campi salva sempre stringhe — un checkbox a `true` non combinerebbe mai con il
// "true" scritto in configurazione. Qui il confronto è normalizzato, e quel caso è IL caso d'uso:
// "questa azione si sblocca solo se la verifica precedente è andata a buon fine".
// ---------------------------------------------------------------------------------------------

// Oggetto singolo, lista o niente: da qui in poi si ragiona sempre su una lista.
export const normalizeConditions = (raw) => {
  if (!raw) return []
  const list = Array.isArray(raw) ? raw : [raw]
  return list.filter(c => c && typeof c === 'object' && c.field)
}

// Confronto normalizzato: `true` (checkbox) e `"true"` (editor) sono lo stesso valore, come `3` e "3".
const sameValue = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase()

// "Valorizzato": una lista ha almeno un elemento, un checkbox è true, una stringa non è vuota.
const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined) return false
  return String(value).trim() !== ''
}

// Una clausola sola. Un operatore assente (clausola malformata) non blocca: la configurazione
// sbagliata non deve spegnere un pulsante senza che nessuno capisca perché.
export const isConditionMet = (values, condition) => {
  const current = values?.[condition.field]

  if (condition.isSet !== undefined) return hasValue(current) === (condition.isSet !== false)
  if (condition.notValue !== undefined) return !sameValue(current, condition.notValue)
  if (Array.isArray(condition.values)) {
    return Array.isArray(current)
      ? current.some(v => condition.values.some(cv => sameValue(v, cv)))
      : condition.values.some(cv => sameValue(current, cv))
  }
  if (condition.value !== undefined) {
    // Su un multiselect la condizione è soddisfatta se il valore è fra quelli scelti
    return Array.isArray(current)
      ? current.some(v => sameValue(v, condition.value))
      : sameValue(current, condition.value)
  }

  return true
}

// Tutte le clausole in AND. `unmet` serve a dire all'utente COSA manca, non solo che manca qualcosa.
export const evaluateConditions = (values, conditions) => {
  const list = normalizeConditions(conditions)
  const unmet = list.filter(c => !isConditionMet(values, c))
  return { ok: unmet.length === 0, unmet }
}

// Riga leggibile per tooltip e chip degli editor. `fieldLabel` è l'etichetta del campo (o il nome);
// `words` porta le parole tradotte (chi chiama ha l'i18n, queste funzioni no).
export const describeCondition = (condition, fieldLabel, words = {}) => {
  const name = fieldLabel || condition.field
  const w = { yes: 'true', no: 'false', filled: 'ok', empty: '—', ...words }
  // Un checkbox vale true/false: "= sì" si legge, "= true" no
  const show = (value) => (sameValue(value, 'true') ? w.yes : sameValue(value, 'false') ? w.no : String(value))

  if (condition.isSet !== undefined) return `${name}: ${condition.isSet === false ? w.empty : w.filled}`
  if (condition.notValue !== undefined) return `${name} ≠ ${show(condition.notValue)}`
  if (Array.isArray(condition.values)) return `${name} ∈ ${condition.values.map(show).join(' / ')}`
  if (condition.value !== undefined) return `${name} = ${show(condition.value)}`
  return name
}

// Campi checkbox di validazione: li scrive la verifica AI (o un superadmin), l'utente non può
// compilarli a mano. Stesso criterio del Backend (LoadValidationFields filtra type == checkbox):
// senza il vincolo sul tipo verrebbe marcato anche idProject, che è readonly ma non c'entra.
export const requiresVerification = (field) =>
  field.type === 'checkbox' && (field.readonly === true || Array.isArray(field.editableByRoles))
